/**
 * The recipe is the set of knobs a process technician actually touches on the
 * tool, plus the chamber/consumable state that drifts underneath them. Keeping
 * drift in the same structure is deliberate: most of the diagnostic work in a
 * fab is separating "someone changed a setpoint" from "the chamber moved".
 */

export interface Recipe {
  /** Chamber pressure setpoint [mTorr]. */
  pressureMtorr: number
  /** Source RF power [W] - sets plasma density, so ion flux. */
  sourcePowerW: number
  /** Bias RF power [W] - sets sheath voltage, so ion energy. */
  biasPowerW: number
  /** Total feed gas flow [sccm]. */
  flowSccm: number
  /** Fraction of the feed that is the etchant source gas (CF4); balance is Ar. */
  cf4Fraction: number
  /** Electrostatic chuck / wafer temperature setpoint [C]. */
  wafertempC: number
  /** Fraction of the wafer surface that is open (not covered by mask). */
  openAreaFraction: number
}

/**
 * Chamber condition: everything that affects the result which the technician
 * does NOT set.
 *
 * This is the other half of the job. A recipe is under change control and
 * rarely moves; the chamber moves constantly, and most real troubleshooting is
 * separating "someone changed a setpoint" from "the chamber drifted underneath
 * an unchanged one". Every field here reaches the wafer through the physics -
 * none of them scale an answer directly.
 */
export interface ChamberState {
  /** Wall condition, 0 = freshly cleaned and seasoned, 1 = heavily loaded. */
  wallSeasoning: number
  /** Base pressure achieved by the pump stack [mTorr]; rises as the pump ages. */
  basePressureMtorr: number
  /** Leak-up rate [mTorr/min]; a real leak shows here. */
  leakUpRate: number
  /**
   * Source and bias RF each have their own generator and their own match
   * network, and they must be modelled separately.
   *
   * Sharing one efficiency between them hides the most diagnostic fact about a
   * detuned match: ion energy is bias watts divided by ion current, and if a
   * single efficiency scaled both, it would cancel and the fault would be
   * invisible in the energy. Detuning the SOURCE match alone makes fewer ions
   * while the bias supply delivers the same watts, so each remaining ion
   * arrives HARDER - a slow etch with hot ions, which no gas-supply fault can
   * produce.
   */
  sourceMatchEfficiency: number
  biasMatchEfficiency: number
  /**
   * Fraction of the mass flow controller setpoint actually delivered. An MFC
   * that reads 100 sccm while passing 70 is one of the most common and most
   * confusing faults on a gas panel: every displayed value looks correct.
   */
  flowDelivery: number
  /** Difference between chuck setpoint and true wafer temperature [C]. */
  chuckTempOffsetC: number
  /** Focus ring erosion, 0 = new, 1 = past its replacement limit. */
  focusRingWear: number
  /** Wafers processed since the last chamber clean. */
  wafersSinceClean: number
  /** Hours on the pump stack since service. */
  pumpHours: number
  /** RF hours on the current focus ring and electrode set. */
  rfHours: number

  // --- supplied by the house, and NOT repairable at this tool ---
  //
  // These are separate fields rather than additions to the ones above, and the
  // separation is the whole reason cross-module diagnosis works. The chamber
  // gauge cannot tell `leakUpRate` from `houseLeakUpRate` - both are air
  // getting in - so the symptom is genuinely ambiguous. But a leak check on
  // this chamber's own seals restores only the first, so the counterfactual is
  // not ambiguous at all, and the diagnosis machinery reports that without
  // knowing anything about house systems.

  /** Leak-up the shared process vacuum header adds here [mTorr/min]. */
  houseLeakUpRate: number
  /** Background pressure the shared header adds here [mTorr]. */
  houseBasePressureMtorr: number
  /** Chuck offset forced by warm cooling water - the chiller cannot reject
   *  heat into a loop that is already warm, so the chuck floats up [C]. */
  houseChuckTempOffsetC: number
  /**
   * Source match efficiency lost to warm cooling water.
   *
   * RF generators and match networks are water-cooled, and they are cooled by
   * the same house loop as the chuck. Warm coolant raises component
   * temperature, capacitance drifts, and the match sits off its tuned point -
   * so a facilities problem arrives at the etcher looking exactly like a
   * detuned match, which is a repair somebody will happily spend an hour on.
   */
  houseMatchLoss: number
}

/** Source match efficiency the tool actually achieves. */
export const effectiveSourceMatch = (c: ChamberState): number =>
  Math.max(c.sourceMatchEfficiency - c.houseMatchLoss, 0.05)

/** Leak-up rate the gauge on this chamber actually shows [mTorr/min]. */
export const effectiveLeakUp = (c: ChamberState): number => c.leakUpRate + c.houseLeakUpRate

/** Base pressure the gauge on this chamber actually shows [mTorr]. */
export const effectiveBasePressure = (c: ChamberState): number =>
  c.basePressureMtorr + c.houseBasePressureMtorr

/** True wafer temperature offset from setpoint [C]. */
export const effectiveChuckOffset = (c: ChamberState): number =>
  c.chuckTempOffsetC + c.houseChuckTempOffsetC

export const DEFAULT_RECIPE: Recipe = {
  pressureMtorr: 20,
  sourcePowerW: 600,
  biasPowerW: 100,
  flowSccm: 200,
  cf4Fraction: 0.7,
  wafertempC: 40,
  openAreaFraction: 0.35,
}

/** A chamber fresh from preventive maintenance and qualified. */
export const NOMINAL_CHAMBER: ChamberState = {
  wallSeasoning: 0.15,
  basePressureMtorr: 0.5,
  leakUpRate: 0.2,
  sourceMatchEfficiency: 0.97,
  biasMatchEfficiency: 0.97,
  flowDelivery: 1.0,
  chuckTempOffsetC: 0,
  focusRingWear: 0.05,
  wafersSinceClean: 4,
  pumpHours: 60,
  rfHours: 40,
  houseLeakUpRate: 0,
  houseBasePressureMtorr: 0,
  houseChuckTempOffsetC: 0,
  houseMatchLoss: 0,
}

export const cloneChamber = (c: ChamberState): ChamberState => ({ ...c })

/** Ranges the HMI will allow. Outside these the tool would interlock. */
export const RECIPE_LIMITS: Record<keyof Recipe, { min: number; max: number; step: number; unit: string; label: string }> = {
  pressureMtorr: { min: 2, max: 200, step: 1, unit: 'mTorr', label: 'Chamber pressure' },
  sourcePowerW: { min: 100, max: 2000, step: 25, unit: 'W', label: 'Source RF power' },
  biasPowerW: { min: 0, max: 600, step: 10, unit: 'W', label: 'Bias RF power' },
  flowSccm: { min: 10, max: 500, step: 5, unit: 'sccm', label: 'Total gas flow' },
  cf4Fraction: { min: 0.05, max: 1.0, step: 0.05, unit: '', label: 'CF4 fraction' },
  wafertempC: { min: -20, max: 120, step: 5, unit: '°C', label: 'Wafer temperature' },
  openAreaFraction: { min: 0.02, max: 1.0, step: 0.01, unit: '', label: 'Open area' },
}

/**
 * Named recipes the modules and the assistant can call up by key.
 *
 * The notes describe what the solver actually does, measured - not what the
 * textbook says ought to happen. Two of these produce a fast edge by completely
 * different mechanisms, which is the point: the same thickness map can have more
 * than one cause, and telling them apart is the diagnostic skill.
 */
export const RECIPE_PRESETS: Record<string, { label: string; note: string; recipe: Recipe }> = {
  baseline: {
    label: 'BASELINE-SI-01',
    note: 'Qualified centre-of-window recipe: 204 nm/min at 4.3% non-uniformity, 3.7:1. Start here.',
    recipe: { ...DEFAULT_RECIPE },
  },
  lowPressure: {
    label: 'LO-P STARVED',
    note:
      'Pressure down to 6 mTorr: 116 nm/min at 10.9%. Too little feed in the chamber to make ' +
      'fluorine, so demand outruns supply (Da 1.15) and the edge, which sees the gas first, runs ' +
      '19% fast.',
    recipe: { ...DEFAULT_RECIPE, pressureMtorr: 6 },
  },
  highPressure: {
    label: 'HI-P DIFFUSION-LIMITED',
    note:
      'Pressure up to 90 mTorr: 201 nm/min at 8.6%. Plenty of fluorine (Da 0.20), but diffusion ' +
      'scales as 1/p so it cannot reach the wafer centre fast enough. Fast edge again, 13% - ' +
      'opposite cause, same signature.',
    recipe: { ...DEFAULT_RECIPE, pressureMtorr: 90 },
  },
  highBias: {
    label: 'HI-BIAS ANISOTROPIC',
    note:
      'Bias to 350 W: 227 nm/min but 20% non-uniform and selectivity down to 1.3:1. The extra ' +
      'demand drives the etch transport-limited (Da 1.22). The wrong way to chase rate.',
    recipe: { ...DEFAULT_RECIPE, biasPowerW: 350 },
  },
  smallOpenArea: {
    label: 'LOW-LOAD PRODUCT',
    note:
      'Only 4% open area: 308 nm/min, half again as fast on an unchanged recipe, because far ' +
      'less silicon is competing for the same fluorine. Uniformity improves to 1.8% for the same ' +
      'reason. This is the loading effect, and it is why a recipe qualified on one product drifts ' +
      'on another.',
    recipe: { ...DEFAULT_RECIPE, openAreaFraction: 0.04 },
  },
  starvedFlow: {
    label: 'LO-FLOW EXCURSION',
    note:
      'Mass flow controller down to 20 sccm from 200: collapses to 72 nm/min at 28% ' +
      'non-uniformity, with 78% of the feed dissociated and Da above 2. The fluorine budget is ' +
      'set by what the MFC lets in, and here it has become the binding constraint.',
    recipe: { ...DEFAULT_RECIPE, flowSccm: 20 },
  },
}

export const cloneRecipe = (r: Recipe): Recipe => ({ ...r })
